import React from "react";
import { useNavigate } from "react-router-dom";

const customCardStyles = `
  .kpi-card-wrapper {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    border-radius: 20px;
    overflow: hidden;
    position: relative;
    border: none;
    cursor: pointer;
  }
  .kpi-card-wrapper:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
  }
  .kpi-card-inner {
    padding: 12px;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    z-index: 2;
  }
  .kpi-glass-blob {
    position: absolute;
    width: 150px;
    height: 150px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    top: -50px;
    right: -50px;
    z-index: 1;
  }
  .kpi-label {
    font-size: 0.65rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 6px;
  }
  .kpi-value {
    font-size: 2.0rem;
    font-weight: 600;
    line-height: 1;
    color: #ffffff;
    margin-bottom: 2px;
  }
  .kpi-subtext {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
  }
  .kpi-icon-container {
    align-self: flex-end;
    background: rgba(255, 255, 255, 0.15);
    padding: 12px;
    border-radius: 12px;
    backdrop-filter: blur(4px);
    margin-top: 6px;
  }
  .gradient-blue { background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%); }
  .gradient-green { background: linear-gradient(135deg, #10b981 0%, #065f46 100%); }
  .gradient-purple { background: linear-gradient(135deg, #8b5cf6 0%, #5b21b6 100%); }
  .gradient-orange { background: linear-gradient(135deg, #f59e0b 0%, #b45309 100%); }
`;

export const DashboardStatsCards = ({
  dashboardData,
  currentMonthRevenue = 0,
  lastMonthRevenue = 0,
  visibleCardKeys,
}) => {
  const navigate = useNavigate();

  if (!dashboardData) return null;

  const cards = [
    {
      key: "todayCheckIn",
      label: "Today Check-In",
      value: dashboardData.TotalCheckins,
      subtext: "Guests arriving today",
      colorClass: "gradient-blue",
      path: "/today-check-in-report",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      )
    },
    {
      key: "todayBookingRevenue",
      label: "Today's Booking Revenue",
      value: `₹${Number(dashboardData.TotalAmount || 0).toLocaleString("en-IN")}`,
      subtext: "Earning tracked today",
      colorClass: "gradient-green",
      path: "/today-booking-revenue-report",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="2" />
          <path d="M6 12h.01M18 12h.01" />
        </svg>
      )
    },
    {
      key: "monthlySnapshot",
      label: "Monthly Snapshot",
      value: `₹${Number(currentMonthRevenue || 0).toLocaleString("en-IN")}`,
      subtext: `Previous: ₹${Number(lastMonthRevenue || 0).toLocaleString("en-IN")}`,
      colorClass: "gradient-purple",
      path: null,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="6" width="18" height="12" rx="2" />
          <path d="M7 12h10" />
          <path d="M12 9v6" />
        </svg>
      )
    },
    {
      key: "todayBooking",
      label: "Today Booking",
      value: dashboardData.TotalBookings || 0,
      subtext: "New reservations confirmed",
      colorClass: "gradient-orange",
      path: "/today-booking-list",
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      )
    }
  ];

  const visibleCards = Array.isArray(visibleCardKeys)
    ? cards.filter((card) => visibleCardKeys.includes(card.key))
    : cards;

  return (
    <>
      <style>{customCardStyles}</style>
      <div className="row g-4 mb-4">
        {visibleCards.map((card) => (
          <div key={card.key} className="col-md-6 col-lg-3">
            <div 
              className={`card h-100 kpi-card-wrapper ${card.colorClass}`}
              onClick={() => card.path && navigate(card.path)}
              style={{ cursor: card.path ? "pointer" : "default" }}
            >
              <div className="kpi-glass-blob"></div>
              <div className="kpi-card-inner text-white">
                <div>
                  <div className="kpi-label">{card.label}</div>
                  <div className="kpi-value">{card.value}</div>
                  <div className="kpi-subtext">{card.subtext}</div>
                </div>
                <div className="kpi-icon-container">
                  {card.icon}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
